/**
 * Skeleton Components for Loading States
 * IMPORTANT: Dimensions MUST match actual content to prevent CLS (Cumulative Layout Shift)
 */

import React, { useEffect, useRef } from 'react';
import { View, Animated, StyleSheet, ViewStyle, Platform } from 'react-native';
import { COLORS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

import { tokens } from '@design';
// ============================================================
// DIMENSION CONSTANTS - Must match actual component dimensions
// ============================================================

export const SKELETON_DIMENSIONS = {
  // Stat Cards
  STAT_CARD_HEIGHT: 100,
  STAT_CARD_MIN_WIDTH: 150,

  // Goal Cards
  GOAL_CARD_HEIGHT: 250,
  GOAL_CARD_PADDING: 16,

  // Initiative Cards
  INITIATIVE_CARD_HEIGHT: 160,

  // KPI Cards
  KPI_CARD_HEIGHT: 140,

  // Notification Items
  NOTIFICATION_ITEM_HEIGHT: 88,

  // Table Rows
  TABLE_HEADER_HEIGHT: 48,
  TABLE_ROW_HEIGHT: 56,

  // Chart
  CHART_HEIGHT: 220,

  // Form Fields
  FORM_FIELD_HEIGHT: 70, // label + input

  // Detail Sections
  DETAIL_INFO_ROW_HEIGHT: 40,

  // Header
  HEADER_HEIGHT: 60,
  BREADCRUMB_HEIGHT: 32,
  PAGE_TITLE_HEIGHT: 64,
} as const;

// ============================================================
// Base Skeleton Component
// ============================================================

interface SkeletonProps {
  width?: number | `${number}%`;
  height?: number;
  borderRadius?: number;
  style?: ViewStyle;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 20,
  borderRadius = 4,
  style,
}) => {
  const { isDark } = useTheme();
  const opacity = useRef(new Animated.Value(0.3)).current;
  const skeletonColor = isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"];

  useEffect(() => {
    // Use native driver only on native platforms, not on web
    const useNativeDriver = Platform.OS !== 'web';

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.7,
          duration: 800,
          useNativeDriver,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={[
        {
          width,
          height,
          borderRadius,
          backgroundColor: skeletonColor,
          opacity,
        },
        style,
      ]}
    />
  );
};

// ============================================================
// Dashboard Skeleton - EXACT dimensions to match real dashboard
// ============================================================

export const DashboardSkeleton: React.FC = () => {
  const { colors, isDark } = useTheme();
  const { STAT_CARD_HEIGHT, CHART_HEIGHT, TABLE_ROW_HEIGHT, TABLE_HEADER_HEIGHT } = SKELETON_DIMENSIONS;

  const cardBg = colors.card;
  const borderColor = isDark ? colors.border : tokens.colors.raw.gray["200"];
  const borderColorLight = isDark ? colors.border : tokens.colors.raw.gray["100"];

  return (
    <View style={styles.container}>
      {/* Stats Cards Row - Fixed height container */}
      <View style={[styles.statsRow, { minHeight: STAT_CARD_HEIGHT + 16 }]}>
        {[1, 2, 3, 4].map((i) => (
          <View key={i} style={[styles.statCard, { height: STAT_CARD_HEIGHT, backgroundColor: cardBg }]}>
            <Skeleton height={36} width="45%" style={{ marginBottom: tokens.spacing.absolute["3"] }} />
            <Skeleton height={14} width="75%" />
          </View>
        ))}
      </View>

      {/* Chart Section - Fixed height */}
      <View style={[styles.chartCard, { height: CHART_HEIGHT + 56, backgroundColor: cardBg }]}>
        <Skeleton height={24} width="40%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
        <Skeleton height={CHART_HEIGHT} borderRadius={12} />
      </View>

      {/* Organizations Table - Fixed row heights */}
      <View style={[styles.tableCard, { minHeight: TABLE_HEADER_HEIGHT + (TABLE_ROW_HEIGHT * 5), backgroundColor: cardBg }]}>
        <Skeleton height={24} width="30%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
        {/* Table Header */}
        <View style={[styles.tableRow, { height: TABLE_HEADER_HEIGHT, borderBottomColor: borderColor }]}>
          <Skeleton width="35%" height={16} />
          <Skeleton width="20%" height={16} />
          <Skeleton width="20%" height={16} />
          <Skeleton width="15%" height={16} />
        </View>
        {/* Table Rows */}
        {[1, 2, 3, 4, 5].map((i) => (
          <View key={i} style={[styles.tableRow, { height: TABLE_ROW_HEIGHT, borderBottomColor: borderColorLight }]}>
            <Skeleton width="35%" height={14} />
            <Skeleton width="20%" height={14} />
            <Skeleton width="20%" height={24} borderRadius={12} />
            <Skeleton width="15%" height={14} />
          </View>
        ))}
      </View>
    </View>
  );
};

// ============================================================
// Goals List Skeleton - Fixed card heights
// ============================================================

export const GoalsSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const { GOAL_CARD_HEIGHT, BREADCRUMB_HEIGHT, PAGE_TITLE_HEIGHT } = SKELETON_DIMENSIONS;
  const cardBg = colors.card;

  return (
    <View style={styles.container}>
      {/* Breadcrumb - Fixed height */}
      <View style={{ height: BREADCRUMB_HEIGHT, marginBottom: tokens.spacing.absolute["2"] }}>
        <Skeleton width={150} height={20} />
      </View>

      {/* Page Title - Fixed height */}
      <View style={[styles.headerRow, { height: PAGE_TITLE_HEIGHT }]}>
        <Skeleton width={140} height={40} borderRadius={8} />
        <Skeleton width="45%" height={28} />
      </View>

      {/* Goal Cards - Fixed heights */}
      <View style={styles.cardsGrid}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <View key={i} style={[styles.card, { height: GOAL_CARD_HEIGHT, backgroundColor: cardBg }]}>
            <View style={styles.cardHeader}>
              <Skeleton height={20} width="65%" />
              <Skeleton height={24} width={80} borderRadius={12} />
            </View>
            <Skeleton height={14} width="100%" style={{ marginTop: tokens.spacing.absolute["3"], marginBottom: tokens.spacing.absolute["2"] }} />
            <Skeleton height={14} width="70%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
            <View style={{ flex: 1 }} />
            <View style={styles.cardFooter}>
              <Skeleton height={8} width="100%" borderRadius={4} />
            </View>
            <View style={[styles.cardMeta, { marginTop: tokens.spacing.absolute["3"] }]}>
              <Skeleton height={12} width={100} />
              <Skeleton height={12} width={80} />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

// ============================================================
// Initiatives List Skeleton - Fixed card heights
// ============================================================

export const InitiativesSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const { INITIATIVE_CARD_HEIGHT, BREADCRUMB_HEIGHT, PAGE_TITLE_HEIGHT } = SKELETON_DIMENSIONS;
  const cardBg = colors.card;

  return (
    <View style={styles.container}>
      {/* Breadcrumb */}
      <View style={{ height: BREADCRUMB_HEIGHT, marginBottom: tokens.spacing.absolute["2"] }}>
        <Skeleton width={200} height={20} />
      </View>

      {/* Header */}
      <View style={[styles.headerRow, { height: PAGE_TITLE_HEIGHT }]}>
        <Skeleton width={140} height={40} borderRadius={8} />
        <Skeleton width="50%" height={28} />
      </View>

      {/* Initiative Cards - Fixed heights */}
      <View style={styles.cardsGrid}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <View key={i} style={[styles.card, { height: INITIATIVE_CARD_HEIGHT, backgroundColor: cardBg }]}>
            <View style={styles.cardHeader}>
              <Skeleton height={20} width="70%" />
              <Skeleton height={24} width={70} borderRadius={12} />
            </View>
            <Skeleton height={14} width="90%" style={{ marginTop: tokens.spacing.absolute["3"] }} />
            <Skeleton height={14} width="60%" style={{ marginTop: tokens.spacing.absolute["2"] }} />
            <View style={{ flex: 1 }} />
            <View style={[styles.cardFooter, { marginTop: tokens.spacing.absolute["4"] }]}>
              <Skeleton height={32} width={100} borderRadius={8} />
              <Skeleton height={32} width={100} borderRadius={8} />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

// ============================================================
// KPIs List Skeleton - Fixed card heights
// ============================================================

export const KPIsSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const { KPI_CARD_HEIGHT, BREADCRUMB_HEIGHT, PAGE_TITLE_HEIGHT } = SKELETON_DIMENSIONS;
  const cardBg = colors.card;

  return (
    <View style={styles.container}>
      {/* Breadcrumb */}
      <View style={{ height: BREADCRUMB_HEIGHT, marginBottom: tokens.spacing.absolute["2"] }}>
        <Skeleton width={180} height={20} />
      </View>

      {/* Header */}
      <View style={[styles.headerRow, { height: PAGE_TITLE_HEIGHT }]}>
        <Skeleton width={140} height={40} borderRadius={8} />
        <Skeleton width="45%" height={28} />
      </View>

      {/* KPI Cards - Fixed heights */}
      {[1, 2, 3, 4, 5].map((i) => (
        <View key={i} style={[styles.card, { height: KPI_CARD_HEIGHT, backgroundColor: cardBg }]}>
          <View style={styles.cardHeader}>
            <View style={{ flex: 1 }}>
              <Skeleton height={18} width="80%" style={{ marginBottom: tokens.spacing.absolute["2"] }} />
              <Skeleton height={14} width="50%" />
            </View>
            <Skeleton height={56} width={56} borderRadius={28} />
          </View>
          <View style={{ flex: 1 }} />
          <View style={[styles.cardFooter, { marginTop: tokens.spacing.absolute["3"] }]}>
            <Skeleton height={24} width={90} borderRadius={12} />
            <Skeleton height={24} width={70} borderRadius={12} />
          </View>
        </View>
      ))}
    </View>
  );
};

// ============================================================
// Pending Approvals Skeleton - summary rows + deletion cards
// ============================================================

export const PendingApprovalsSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const { BREADCRUMB_HEIGHT, PAGE_TITLE_HEIGHT } = SKELETON_DIMENSIONS;
  const cardBg = colors.card;

  return (
    <View style={styles.container}>
      {/* Breadcrumb */}
      <View style={{ height: BREADCRUMB_HEIGHT, marginBottom: tokens.spacing.absolute["2"] }}>
        <Skeleton width={220} height={20} />
      </View>

      {/* Header (title + subtitle) */}
      <View style={[styles.headerRow, { height: PAGE_TITLE_HEIGHT }]}>
        <View style={{ flex: 1 }}>
          <Skeleton width={180} height={28} style={{ marginBottom: tokens.spacing.absolute["2"] }} />
          <Skeleton width={120} height={16} />
        </View>
        <Skeleton width={44} height={44} borderRadius={10} />
      </View>

      {/* Summary type cards (icon + label + count badge rows) */}
      {[1, 2, 3, 4].map((i) => (
        <View
          key={`type-${i}`}
          style={{
            height: 76,
            backgroundColor: cardBg,
            borderRadius: tokens.radii.absolute["radius-12"],
            padding: tokens.spacing.absolute["4"],
            marginBottom: tokens.spacing.absolute["3"],
            flexDirection: 'row',
            alignItems: 'center',
            gap: tokens.spacing.absolute["3"],
          }}
        >
          <Skeleton width={44} height={44} borderRadius={10} />
          <View style={{ flex: 1 }}>
            <Skeleton width="55%" height={18} style={{ marginBottom: tokens.spacing.absolute["2"] }} />
            <Skeleton width="35%" height={14} />
          </View>
          <Skeleton width={40} height={28} borderRadius={14} />
        </View>
      ))}

      {/* Deletion request cards (title/meta + two action buttons) */}
      {[1, 2].map((i) => (
        <View
          key={`del-${i}`}
          style={{
            height: 120,
            backgroundColor: cardBg,
            borderRadius: tokens.radii.absolute["radius-12"],
            padding: tokens.spacing.absolute["4"],
            marginBottom: tokens.spacing.absolute["3"],
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["3"] }}>
            <Skeleton width={36} height={36} borderRadius={8} />
            <View style={{ flex: 1 }}>
              <Skeleton width="60%" height={18} style={{ marginBottom: tokens.spacing.absolute["2"] }} />
              <Skeleton width="40%" height={14} />
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute["3"], marginTop: tokens.spacing.absolute["4"] }}>
            <Skeleton width={90} height={36} borderRadius={8} />
            <Skeleton width={90} height={36} borderRadius={8} />
          </View>
        </View>
      ))}
    </View>
  );
};

// ============================================================
// Detail Screen Skeleton - Fixed section heights
// ============================================================

export const DetailSkeleton: React.FC = () => {
  const { colors, isDark } = useTheme();
  const { DETAIL_INFO_ROW_HEIGHT } = SKELETON_DIMENSIONS;
  const cardBg = colors.card;
  const borderColorLight = isDark ? colors.border : tokens.colors.raw.gray["100"];

  return (
    <View style={styles.container}>
      {/* Title Section - Fixed height */}
      <View style={{ minHeight: 72, marginBottom: tokens.spacing.absolute["6"] }}>
        <Skeleton height={28} width="70%" style={{ marginBottom: tokens.spacing.absolute["2"] }} />
        <Skeleton height={16} width="40%" />
      </View>

      {/* Info Card - Fixed row heights */}
      <View style={[styles.card, { minHeight: (DETAIL_INFO_ROW_HEIGHT * 4) + 60, backgroundColor: cardBg }]}>
        <Skeleton height={20} width="30%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
        {[1, 2, 3, 4].map((i) => (
          <View key={i} style={[styles.infoRow, { height: DETAIL_INFO_ROW_HEIGHT }]}>
            <Skeleton height={14} width="30%" />
            <Skeleton height={14} width="50%" />
          </View>
        ))}
      </View>

      {/* Progress Section - Fixed height */}
      <View style={[styles.card, { minHeight: 100, backgroundColor: cardBg }]}>
        <Skeleton height={20} width="25%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
        <Skeleton height={12} width="100%" borderRadius={6} style={{ marginBottom: tokens.spacing.absolute["2"] }} />
        <View style={styles.progressMeta}>
          <Skeleton height={12} width={60} />
          <Skeleton height={12} width={60} />
        </View>
      </View>

      {/* Related Items - Fixed row heights */}
      <View style={[styles.card, { minHeight: 200, backgroundColor: cardBg }]}>
        <Skeleton height={20} width="35%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
        {[1, 2, 3].map((i) => (
          <View key={i} style={[styles.listItem, { height: 52, borderBottomColor: borderColorLight }]}>
            <Skeleton height={16} width="70%" />
            <Skeleton height={24} width={60} borderRadius={12} />
          </View>
        ))}
      </View>
    </View>
  );
};

// ============================================================
// Notifications Skeleton - Fixed item heights
// ============================================================

export const NotificationsSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const { NOTIFICATION_ITEM_HEIGHT } = SKELETON_DIMENSIONS;
  const cardBg = colors.card;

  return (
    <View style={styles.container}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <View key={i} style={[styles.notificationCard, { height: NOTIFICATION_ITEM_HEIGHT, backgroundColor: cardBg }]}>
          <Skeleton height={44} width={44} borderRadius={22} />
          <View style={styles.notificationContent}>
            <Skeleton height={16} width="80%" style={{ marginBottom: tokens.spacing.absolute["2"] }} />
            <Skeleton height={14} width="60%" style={{ marginBottom: tokens.spacing.absolute["2"] }} />
            <Skeleton height={12} width={100} />
          </View>
        </View>
      ))}
    </View>
  );
};

// ============================================================
// Form Skeleton - Fixed field heights
// ============================================================

export const FormSkeleton: React.FC = () => {
  const { FORM_FIELD_HEIGHT } = SKELETON_DIMENSIONS;

  return (
    <View style={styles.container}>
      {/* Form Fields - Fixed heights */}
      {[1, 2, 3, 4].map((i) => (
        <View key={i} style={[styles.formField, { height: FORM_FIELD_HEIGHT }]}>
          <Skeleton height={14} width={100} style={{ marginBottom: tokens.spacing.absolute["2"] }} />
          <Skeleton height={44} width="100%" borderRadius={8} />
        </View>
      ))}

      {/* Buttons - Fixed height */}
      <View style={[styles.cardFooter, { marginTop: tokens.spacing.absolute["6"], height: 44 }]}>
        <Skeleton height={44} width="48%" borderRadius={8} />
        <Skeleton height={44} width="48%" borderRadius={8} />
      </View>
    </View>
  );
};

// ============================================================
// Table Skeleton - Fixed row heights
// ============================================================

export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  const { isDark, colors } = useTheme();
  const { TABLE_HEADER_HEIGHT, TABLE_ROW_HEIGHT } = SKELETON_DIMENSIONS;
  const borderColor = isDark ? colors.border : tokens.colors.raw.gray["200"];
  const borderColorLight = isDark ? colors.border : tokens.colors.raw.gray["100"];

  return (
    <View style={[styles.container, { minHeight: TABLE_HEADER_HEIGHT + (TABLE_ROW_HEIGHT * rows) }]}>
      {/* Table Header - Fixed height */}
      <View style={[styles.tableHeader, { height: TABLE_HEADER_HEIGHT, borderBottomColor: borderColor }]}>
        <Skeleton width="30%" height={16} />
        <Skeleton width="20%" height={16} />
        <Skeleton width="20%" height={16} />
        <Skeleton width="15%" height={16} />
      </View>

      {/* Table Rows - Fixed heights */}
      {Array.from({ length: rows }).map((_, i) => (
        <View key={i} style={[styles.tableRow, { height: TABLE_ROW_HEIGHT, borderBottomColor: borderColorLight }]}>
          <Skeleton width="30%" height={14} />
          <Skeleton width="20%" height={14} />
          <Skeleton width="20%" height={24} borderRadius={12} />
          <Skeleton width="15%" height={14} />
        </View>
      ))}
    </View>
  );
};

// ============================================================
// Pillar-level skeletons used by /strategy
// Small wrappers around <Skeleton/> with fixed dimensions taken from
// SKELETON_DIMENSIONS so each pillar reserves its real height during
// loading and avoids layout shift when content arrives.
// ============================================================

export const StatCardSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => {
  const { colors } = useTheme();
  const cardBg = colors.card;
  const { STAT_CARD_HEIGHT } = SKELETON_DIMENSIONS;
  return (
    <View style={[styles.statsRow, { minHeight: STAT_CARD_HEIGHT + 16 }]}>
      {Array.from({ length: count }).map((_, i) => (
        <View key={i} style={[styles.statCard, { height: STAT_CARD_HEIGHT, backgroundColor: cardBg }]}>
          <Skeleton height={36} width="45%" style={{ marginBottom: tokens.spacing.absolute["3"] }} />
          <Skeleton height={14} width="75%" />
        </View>
      ))}
    </View>
  );
};

export const GaugeCardSkeleton: React.FC<{ height?: number }> = ({ height = 220 }) => {
  const { colors } = useTheme();
  return (
    <View style={[styles.chartCard, { height: height + 56, backgroundColor: colors.card }]}>
      <Skeleton height={20} width="40%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
      <View style={{ alignItems: 'center', justifyContent: 'center', height }}>
        <Skeleton width={140} height={140} borderRadius={70} />
      </View>
    </View>
  );
};

export const DueReadingsSkeleton: React.FC = () => {
  const { colors } = useTheme();
  return (
    <View style={[styles.chartCard, { backgroundColor: colors.card }]}>
      <Skeleton height={20} width="35%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
      <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute["3"] }}>
        {[1, 2, 3].map((i) => (
          <View key={i} style={{ flex: 1, gap: tokens.spacing.absolute["2"] }}>
            <Skeleton height={14} width="60%" />
            <Skeleton height={32} width="40%" />
          </View>
        ))}
      </View>
    </View>
  );
};

export const DonutCardSkeleton: React.FC<{ height?: number }> = ({ height = 260 }) => {
  const { colors } = useTheme();
  return (
    <View style={[styles.chartCard, { height: height + 56, backgroundColor: colors.card }]}>
      <Skeleton height={20} width="35%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
      <View style={{ alignItems: 'center', justifyContent: 'center', height }}>
        <Skeleton width={180} height={180} borderRadius={90} />
      </View>
    </View>
  );
};

export const ComparisonChartSkeleton: React.FC = () => {
  const { colors } = useTheme();
  const { CHART_HEIGHT } = SKELETON_DIMENSIONS;
  return (
    <View style={[styles.chartCard, { height: CHART_HEIGHT + 56, backgroundColor: colors.card }]}>
      <Skeleton height={24} width="40%" style={{ marginBottom: tokens.spacing.absolute["4"] }} />
      <Skeleton height={CHART_HEIGHT} borderRadius={12} />
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    padding: tokens.spacing.absolute["4"],
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  statCard: {
    flex: 1,
    minWidth: SKELETON_DIMENSIONS.STAT_CARD_MIN_WIDTH,
    backgroundColor: COLORS.white,
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    justifyContent: 'center',
  },
  chartCard: {
    backgroundColor: COLORS.white,
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  tableCard: {
    backgroundColor: COLORS.white,
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
  },
  tableHeader: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: 2,
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  tableRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  headerRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
  },
  card: {
    flex: 1,
    minWidth: 280,
    backgroundColor: COLORS.white,
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
  },
  cardHeader: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardFooter: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  cardMeta: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
  infoRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressMeta: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
  listItem: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  notificationCard: {
    flexDirection: 'row-reverse',
    backgroundColor: COLORS.white,
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    marginBottom: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  notificationContent: {
    flex: 1,
  },
  formField: {
    marginBottom: tokens.spacing.absolute["4"],
  },
});

export default Skeleton;
